import { useEffect, useState } from 'react'
import { flushSync } from 'react-dom'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import clsx from 'clsx'
import { OrderFormat } from '@ancon/wildcat-types'
import ArrowRightIcon from '@ancon/wildcat-ui/shared/icons/arrow-right.svg'
import getCheckoutGrandTotalAmount from '@ancon/wildcat-utils/checkout/getCheckoutGrandTotalAmount'
import getFormattedCurrency from '@ancon/wildcat-utils/currency/getFormattedCurrency'

import Button from '../../../app/components/Button'
import BodyText from '../../../app/components/BodyText'
import Spinner from '../../../app/components/Spinner'
import useAppDispatch from '../../../../store/hooks/useAppDispatch'
import useAppSelector from '../../../../store/hooks/useAppSelector'
import useDeviceSize from '../../../app/hooks/useDeviceSize'
import {
  appSetIsCartDrawerVisible,
  appSetIsCartRailCollapsed,
} from '../../../app/store/appSlice'
import { AppRoutes, AppTestIds } from '../../../app/constants'
import { isPreOrderCheckoutSelector } from '../../../preOrder/store/preOrderSelectors'
import {
  checkoutCurrentCheckoutDetailsSelector,
  checkoutCurrentCheckoutPendingSelector,
  currentCheckoutAddItemPendingSelector,
  currentCheckoutCartRailItemQtySelector,
} from '../../store/checkoutSelectors'
import { updateCheckout } from '../../store/checkoutThunks'
import useCartRailVisible from '../../hooks/useCartRailVisible'
import { CartDrawerId, CartRailShowCartButtonId } from '../../constants'

import styles from './CartRail.module.scss'

/**
 * Closed cart: "Show cart", the total and Checkout, as a rail on
 * large desktops and a bottom bar on smaller screens
 */
export default function CartRail() {
  const { t } = useTranslation('common')
  const dispatch = useAppDispatch()
  const router = useRouter()
  const { isLargeDesktop } = useDeviceSize()

  const isVisible = useCartRailVisible()
  const checkout = useAppSelector(checkoutCurrentCheckoutDetailsSelector)
  const isPreOrderCheckout = useAppSelector(isPreOrderCheckoutSelector)
  const itemCount = useAppSelector(currentCheckoutCartRailItemQtySelector)
  const isAddItemPending = useAppSelector(currentCheckoutAddItemPendingSelector)
  const isCheckoutPending = useAppSelector(
    checkoutCurrentCheckoutPendingSelector,
  )

  const [isGoingToCheckout, setIsGoingToCheckout] = useState(false)
  const [isItemAdded, setIsItemAdded] = useState(false)

  // A short bump when something is added, so the collapsed cart shows it.
  // Only while the rail is shown, so a hidden rail doesn't bump later on
  useEffect(() => {
    if (!isVisible) {
      setIsItemAdded(false)
    } else if (isAddItemPending) {
      setIsItemAdded(true)
    }
  }, [isAddItemPending, isVisible])

  if (!isVisible) {
    return null
  }

  function showCart() {
    // The rail goes away as the cart opens, so focus moves to the cart
    flushSync(() => {
      if (isLargeDesktop) {
        dispatch(appSetIsCartRailCollapsed(false))
      } else {
        dispatch(appSetIsCartDrawerVisible(true))
      }
    })

    document.getElementById(CartDrawerId)?.focus()
  }

  async function goToCheckout() {
    // Box pickup times, the minimum order and stock problems are handled by
    // the cart's own "Go to Checkout", so the cart opens for those
    const needsCart =
      !checkout ||
      checkout.orderFormat === OrderFormat.BoxPickup ||
      (checkout.minimumOrderDifferenceAmount?.amount ?? 0) > 0

    if (needsCart) {
      showCart()
      return
    }

    setIsGoingToCheckout(true)

    try {
      await dispatch(updateCheckout({})).unwrap()
      await router.push({ pathname: AppRoutes.Checkout })
    } catch {
      showCart()
    } finally {
      setIsGoingToCheckout(false)
    }
  }

  const total =
    checkout && !isPreOrderCheckout
      ? getCheckoutGrandTotalAmount(checkout)
      : null

  const formattedTotal = total
    ? getFormattedCurrency(total.amount, total.currency)
    : null

  const isTotalPending = isAddItemPending && !isPreOrderCheckout

  const itemsCountText = t('cart.itemsCount', { count: itemCount })

  // Read out once the total is in, e.g. "3 items, 245 kr"
  const statusText =
    formattedTotal && !isTotalPending
      ? t('cart.railStatus', { items: itemsCountText, total: formattedTotal })
      : itemsCountText

  return (
    <aside
      className={clsx(styles.rail, { [styles.itemAdded]: isItemAdded })}
      aria-label={t('cart.summaryAria')}
      onAnimationEnd={() => setIsItemAdded(false)}
    >
      <button
        type="button"
        id={CartRailShowCartButtonId}
        className={styles.expand}
        onClick={showCart}
        aria-controls={CartDrawerId}
        aria-expanded={false}
        data-testid={AppTestIds.Cart.RailShowCartButton}
      >
        <span>{t('cart.showCart')}</span>
      </button>
      <div className={styles.summary}>
        <BodyText as="span" color="body-1" className={styles.small}>
          {t('cart.yourOrder')}
        </BodyText>
        {/* The total waits for core; the count above it does not */}
        {isTotalPending ? (
          <span role="img" aria-label={t('cart.updatingTotal')}>
            <Spinner size="small" />
          </span>
        ) : (
          formattedTotal && <strong>{formattedTotal}</strong>
        )}
        <BodyText as="span" color="body-1" className={styles.small}>
          {itemsCountText}
        </BodyText>
      </div>
      <span role="status" className={styles.status}>
        {statusText}
      </span>
      {!isPreOrderCheckout && (
        <Button
          className={styles.checkout}
          data-testid={AppTestIds.Cart.RailCheckoutButton}
          onClick={goToCheckout}
          loading={isGoingToCheckout}
          disabled={isCheckoutPending}
        >
          {t('cart.checkout')}
          <ArrowRightIcon aria-hidden />
        </Button>
      )}
    </aside>
  )
}
